<template>
  <a-popover
    placement="bottomLeft"
    :overlayClassName="'right-popover'"
    v-model:visible="popoverVisible"
  >
    <slot>
      <div class="avatar">
        <Avatar
          size="32"
          class="editImg"
          :url="previewFile(userInfo?.avatar)"
          :name="userInfo?.name"
        >
        </Avatar>
      </div>
    </slot>
    <template #content>
      <div class="content" ref="dropdown">
        <div class="item">
          <div class="item-avatar">
            <Avatar
              size="50"
              class="editImg"
              :url="previewFile(userInfo?.avatar)"
              :name="userInfo?.name"
            >
            </Avatar>
          </div>
          <div class="item-first">
            {{ userInfo?.name }}
          </div>
        </div>
        <a-divider />

        <a-popover
          placement="rightTop"
          trigger="hover"
          :overlayClassName="'avatar-popover'"
          :getPopupContainer="getOrgPopoverContainer"
          v-model:visible="organizationPopoverVisible"
        >
          <div class="item" style="width: 253px">
            <div class="item-avatar">
              <Icon code="xe67d;" style="margin-right: 12px" :size="16"> </Icon>
            </div>
            <div class="item-content">切换团队</div>
          </div>
          <template #content>
            <div style="flex: 1; overflow-y: auto;">
              <div
                class="item"
                :class="{ current: organizationId === o.id }"
                v-for="o in organization"
                :key="o.id"
                @click="goChangeOrganization(o.id)"
              >
                <div class="item-avatar">
                  <Avatar
                    size="40"
                    class="editImg"
                    :name="o.organizationName"
                    :url="previewFile(o.logo)"
                  >
                  </Avatar>
                </div>
                <div class="item-content">
                  <div class="content-one">{{ o.organizationName }}</div>
                  <span class="free" :style="getAccountTypeStyle(o)">{{
                    getAccountTypeDesc(o)
                  }}</span>
                </div>
                <div class="current-mark" v-if="organizationId === o.id">
                  <CheckOutlined />
                </div>
              </div>
            </div>
            <a-divider />
            <div class="item-add flex-shrink-0" @click="openCreateModal">
              <div class="item-avatar">
                <Icon code="xe891;" :size="15" color="#268feb"> </Icon>
              </div>
              <div class="create-content">创建团队</div>
            </div>
          </template>
        </a-popover>

        <div class="item" @click="goHome">
          <div class="item-avatar">
            <Icon code="xe609;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">官网首页</div>
        </div>

        <a-divider v-if="canManage" />
        <a-tooltip
          v-if="canManage"
          title="个人空间不提供成员邀请功能。您可以选择创建团队账户，随后邀请同事加入，以便团队协作。"
          placement="topLeft"
          :visible="inviteTooltipVisible && isPersonalOrg"
          @visibleChange="(v: boolean) => (inviteTooltipVisible = v)"
        >
          <div
            class="item"
            :class="{ disabled: userInfo?.organizationType === 'personal' }"
            @click="handleInviteVisible(true)"
          >
            <div class="item-avatar">
              <Icon code="xe643;" style="margin-right: 12px" :size="16"> </Icon>
            </div>
            <div class="item-content">邀请成员</div>
          </div>
        </a-tooltip>
        <a-divider />
        <div class="item" @click="goPersonalSettings">
          <div class="item-avatar">
            <Icon code="xe634;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">账户设置</div>
        </div>
        <a-divider v-if="canManage" />
        <div class="item" @click="logout">
          <div class="item-avatar">
            <Icon code="xe892;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">退出登录</div>
        </div>
      </div>
    </template>
  </a-popover>
  <InviteModal
    :visible="inviteVisible"
    @close="handleInviteVisible(false)"
    :inviteToken="inviteToken"
  />
  <Modal v-model:visible="createVisible" title="创建团队" @ok="handleOkCreate">
    <a-form
      labelAlign="left"
      ref="createFormRef"
      :labelCol="{ span: 4 }"
      :wrapperCol="{ span: 20 }"
      :model="createForm"
    >
      <BasicFormItem
        v-for="item in createDefs"
        :key="item.key"
        :item="item"
        v-model:value="createForm[item.key]"
      />
    </a-form>
  </Modal>
  <StatusModal v-model:visible="creatingModalVisible" text="正在创建账户..." />
  <SettingModal :initTab="settingType" v-model:visible="settingVisible"></SettingModal>
</template>

<script setup lang="ts">
import { CheckOutlined } from '@ant-design/icons-vue'
import { Icon } from '@elecmap/icon'
import { Avatar, Modal, BasicFormItem } from '@elecmap/elecmap-ui'
import InviteModal from '@elecmap/shared/src/components/InviteModal.vue'
import { previewFile } from '@elecmap/api/src/cad/file'
import SettingModal from '@/views/setting/setting-modal.vue'
import StatusModal from '@elecmap/shared/src/components/StatusModal.vue'
import { useOrganizationDropdown } from './use-organization-dropdown'

const {
  dropdown,
  popoverVisible,
  organizationPopoverVisible,
  userInfo,
  isPersonalOrg,
  canManage,
  organization,
  organizationId,
  getAccountTypeDesc,
  getAccountTypeStyle,
  goChangeOrganization,
  inviteVisible,
  inviteToken,
  handleInviteVisible,
  inviteTooltipVisible,
  goHome,
  logout,
  settingVisible,
  settingType,
  goPersonalSettings,
  createFormRef,
  createVisible,
  creatingModalVisible,
  createForm,
  createDefs,
  openCreateModal,
  handleOkCreate,
} = useOrganizationDropdown()

function getOrgPopoverContainer() {
  return dropdown.value as HTMLElement
}

defineExpose({
  openCreateModal,
})
</script>

<style lang="less" scoped>
.avatar {
  width: 32px;
  height: 32px;
  background-color: #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
}
</style>

<style lang="less">
.ant-popover-inner-content {
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
